import type { SchemaClient } from '../client'
import { SupplySelectionSchema } from './school-supply-menu'
export interface SupplyCasePolicy {
  menuId: string
  menuName: string
  minimumCases: number
}
/** Menu minimums combine cases across all selected SKUs; they never multiply quantities. */
export function supplyCaseShortfalls(
  lines: ReadonlyArray<{
    quantity: number
    casePolicies?: SupplyCasePolicy[]
    isCase?: boolean
  }>,
) {
  const menus = new Map<string, SupplyCasePolicy & { cases: number }>()
  for (const line of lines) {
    if (line.quantity <= 0) {
      continue
    }
    for (const policy of line.casePolicies ?? []) {
      const current = menus.get(policy.menuId) ?? { ...policy, cases: 0 }
      current.minimumCases = Math.max(current.minimumCases, policy.minimumCases)
      if (line.isCase) {
        current.cases += line.quantity
      }
      menus.set(policy.menuId, current)
    }
  }
  return [...menus.values()]
    .filter((value) => value.cases < value.minimumCases)
    .map((value) => ({
      ...value,
      message: `${value.menuName} requires at least ${value.minimumCases} cases total. Mix and match items; add ${value.minimumCases - value.cases} more case${value.minimumCases - value.cases === 1 ? '' : 's'}.`,
    }))
}
/** True when one ordered unit is a supplier case. An ITEM sale of a case SKU
 * is one piece of that case and never counts toward a case minimum. */
export function isSupplyCase(pack: unknown, saleUnit?: 'PACK' | 'ITEM' | null): boolean {
  if (saleUnit === 'ITEM') {
    return false
  }
  const outer = Array.isArray(pack) ? (pack[0] as { unit?: unknown }) : null
  return typeof outer?.unit === 'string' && ['case', 'cases', 'cs'].includes(outer.unit.toLowerCase().trim())
}
/** Template case requirements stay live for every school offered those items,
 * including schools using the national fallback. Prices and selection stay school-owned. */
export async function readSupplyCasePolicies(db: SchemaClient): Promise<Map<string, SupplyCasePolicy[]>> {
  const templates = await db.menuTemplateLine.findMany({
    where: { isArchived: false, OR: [{ program: 'SUPPLIES' }, { lineType: 'BACKUP_SHELF_STABLE' }] },
    select: { id: true, name: true, settings: true },
    orderBy: { id: 'asc' },
  })
  const policies = new Map<string, SupplyCasePolicy[]>()
  for (const template of templates) {
    const settings = template.settings as { bulkSupplyMenu?: unknown } | null
    const parsed = SupplySelectionSchema.safeParse(settings?.bulkSupplyMenu)
    if (!parsed.success || !parsed.data.minimumCases) {
      continue
    }
    for (const item of parsed.data.items.filter((item) => item.available)) {
      const existing = policies.get(item.supplyItemId) ?? []
      existing.push({ menuId: template.id, menuName: template.name, minimumCases: parsed.data.minimumCases })
      policies.set(item.supplyItemId, existing)
    }
  }
  return policies
}
